import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, X } from 'lucide-react';
import { fmt } from '@nyce/admin-kit';
import { Button, Chip, Select, Switch } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, TableScroller, useAsync } from '../../components/common';
import { useTargets } from '../../identity/session';
import {
  buddyFor,
  type DrillKind,
  type UsageGroup,
  type UsageGroupBy,
  type UsagePeriodName,
  type UsageQuery,
  type UsageSeries,
} from '../api/usage';
import { BarChart, LineChart } from '@nyce/admin-kit/console';
import { KpiGrid, LimitMeter, PERIODS, agentShare, compact, money, pct, periodLabel } from '../shared';
import '../../pages/statistics/statistics.css';

const GROUP_LABELS: Record<UsageGroupBy, string> = {
  organization: 'Organization',
  tenant: 'AUTH tenant',
  system: 'System',
  user: 'User',
  model: 'Model',
};

/** Groupings with a drill-down (and, except the AUTH tenant, a budget). */
const DRILL: Partial<Record<UsageGroupBy, DrillKind>> = {
  organization: 'organization',
  system: 'system',
  tenant: 'tenant',
};
const hasLimit = (g: UsageGroupBy) => g === 'organization' || g === 'system';

const DAY = 86_400_000;
const isDate = (s: string | null) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** `period=custom` needs both dates, from ≤ to, at most 366 days (buddy3's limit). */
export function customRangeError(from: string, to: string): string | null {
  if (!isDate(from) || !isDate(to)) return 'Pick both dates.';
  const days = (Date.parse(to) - Date.parse(from)) / DAY + 1;
  if (days < 1) return 'The start is after the end.';
  if (days > 366) return 'At most 366 days.';
  return null;
}

/** The URL holds the view (`period`, `from`, `to`, `compare`, `groupBy`, `drill=<kind>:<id>`), so it can be shared. */
function useUsageParams() {
  const [params, setParams] = useSearchParams();
  const period = (
    PERIODS.some((p) => p.value === params.get('period')) ? params.get('period') : 'month'
  ) as UsagePeriodName;
  const groupRaw = params.get('groupBy') ?? '';
  const groupBy = (groupRaw in GROUP_LABELS ? groupRaw : 'organization') as UsageGroupBy;
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const compare = params.get('compare') === '1';
  const drillRaw = params.get('drill') ?? '';
  const [kind, ...rest] = drillRaw.split(':');
  const drill =
    kind && rest.length && (['organization', 'system', 'tenant'] as string[]).includes(kind)
      ? { kind: kind as DrillKind, id: rest.join(':') }
      : null;
  const set = (patch: Record<string, string | null>) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        for (const [k, v] of Object.entries(patch))
          if (v == null || v === '') next.delete(k);
          else next.set(k, v);
        return next;
      },
      { replace: true }
    );
  return { period, groupBy, from, to, compare, drill, set };
}

/** Hour, weekday, model and request-kind charts (overview and drill-down). */
function SeriesCharts({ s, timezone }: { s: UsageSeries; timezone?: string }) {
  return (
    <div className="bd-grid">
      <BarChart
        title={`Questions by hour of day${timezone ? ` (${timezone})` : ''}`}
        bars={(s.hourly ?? []).map((h) => ({
          key: String(h.hour),
          label: `${String(h.hour).padStart(2, '0')}:00`,
          v: h.questions,
        }))}
        format={(v) => fmt(v)}
      />
      <BarChart
        title="Questions by weekday"
        bars={(s.weekday ?? []).map((w) => ({
          key: String(w.weekday),
          label: w.name ?? String(w.weekday),
          v: w.questions,
        }))}
        format={(v) => fmt(v)}
      />
      <BarChart
        title="Real tokens by model"
        bars={[...(s.byModel ?? [])]
          .sort((a, b) => b.tokens - a.tokens)
          .map((m) => ({ key: m.model, label: m.model, v: m.tokens }))}
        format={compact}
      />
      <BarChart
        title="Requests by kind"
        bars={[...(s.byKind ?? [])]
          .sort((a, b) => b.requests - a.requests)
          .map((k) => ({ key: k.kind, label: k.kind, v: k.requests }))}
        format={(v) => fmt(v)}
      />
    </div>
  );
}

/** Daily tokens (one unit, one axis) and daily questions. */
function DailyCharts({ s, from, to }: { s: UsageSeries; from: string; to: string }) {
  const daily = s.daily ?? [];
  const t = (d: string) => Date.parse(`${d}T00:00:00Z`);
  return (
    <div className="bd-grid">
      <LineChart
        title="Tokens per day"
        from={t(from)}
        to={t(to) - DAY}
        stepMs={DAY}
        format={compact}
        series={[
          {
            key: 'nyce',
            label: 'NYCE tokens',
            points: daily.map((d) => ({ t: t(d.date), v: d.weightedTokens ?? null })),
          },
          { key: 'real', label: 'Real tokens', points: daily.map((d) => ({ t: t(d.date), v: d.tokens })) },
        ]}
      />
      <BarChart
        title="Questions per day"
        bars={daily.map((d) => ({ key: d.date, label: d.date, v: d.questions ?? 0 }))}
        format={(v) => fmt(v)}
      />
    </div>
  );
}

function GroupTable({
  groups,
  groupBy,
  compare,
  onOpen,
}: {
  groups: UsageGroup[];
  groupBy: UsageGroupBy;
  compare: boolean;
  onOpen?: (g: UsageGroup) => void;
}) {
  const limits = hasLimit(groupBy);
  const showTenant = groupBy === 'system' || groupBy === 'user';
  const anyAgent = groups.some((g) => (g.totals ?? g).agentTokens != null);
  return (
    <div className="nyk-card">
      <div className="nyk-card-head">
        <h2>Per {GROUP_LABELS[groupBy].toLowerCase()}</h2>
        <span className="nyk-ui-hint">{groups.length}</span>
      </div>
      <TableScroller>
        <table className="nyk-ui-table nyk-cards" data-testid="buddy-groups">
          <thead>
            <tr>
              <th>{GROUP_LABELS[groupBy]}</th>
              {showTenant && <th>Tenant</th>}
              <th className="nyk-ui-num">Questions</th>
              <th className="nyk-ui-num">Users</th>
              <th className="nyk-ui-num">NYCE tokens</th>
              <th className="nyk-ui-num">Cost</th>
              {anyAgent && <th className="nyk-ui-num">Agent share</th>}
              {compare && <th className="nyk-ui-num">Questions before</th>}
              {limits && <th>Month limit</th>}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const t = g.totals ?? g;
              const share = agentShare(t);
              return (
                <tr key={g.id} data-group={g.id}>
                  <td className="nyk-card-title">
                    {onOpen ? (
                      <button type="button" className="nyk-link" onClick={() => onOpen(g)}>
                        {g.name}
                      </button>
                    ) : (
                      <b>{g.name}</b>
                    )}
                    {g.isAgent && (
                      <Chip size="sm" className="nyk-tone-blue">
                        agent
                      </Chip>
                    )}
                  </td>
                  {showTenant && <td data-label="Tenant">{g.tenantName ?? g.tenantId ?? ''}</td>}
                  <td className="nyk-ui-num" data-label="Questions">
                    {fmt(g.questions)}
                  </td>
                  <td className="nyk-ui-num" data-label="Users">
                    {fmt(t.humanUsers ?? g.activeUsers)}
                  </td>
                  <td className="nyk-ui-num" data-label="NYCE tokens">
                    {compact(g.weightedTokens)}
                  </td>
                  <td className="nyk-ui-num" data-label="Cost">
                    {money(g.cost)}
                  </td>
                  {anyAgent && (
                    <td className="nyk-ui-num" data-label="Agent share">
                      {pct(share)}
                    </td>
                  )}
                  {compare && (
                    <td className="nyk-ui-num" data-label="Questions before">
                      {t.previous ? fmt(t.previous.questions) : '—'}
                    </td>
                  )}
                  {limits && (
                    <td data-label="Month limit">
                      <LimitMeter
                        used={g.usedThisMonth}
                        limit={g.monthlyLimit}
                        unlimited={g.isUnlimited}
                        projected={g.projectedThisMonth}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroller>
      {!groups.length && <EmptyState title="No usage in this period." />}
    </div>
  );
}

function DrillDown({
  target,
  kind,
  id,
  query,
  onClose,
}: {
  target: string;
  kind: DrillKind;
  id: string;
  query: UsageQuery;
  onClose: () => void;
}) {
  const res = useAsync(
    () => buddyFor(target).detail(kind, id, query),
    [target, kind, id, JSON.stringify(query)]
  );
  const d = res.data;
  // It opens below the group table, which can be long: bring it into view (once per scope).
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [kind, id]);
  const children = d?.bySystem?.length
    ? { title: 'Systems', rows: d.bySystem.map((s) => ({ key: s.systemId, ...s })) }
    : d?.byTenant?.length
      ? { title: 'AUTH tenants', rows: d.byTenant.map((t) => ({ key: t.tenantId, ...t })) }
      : null;
  return (
    <section
      ref={ref}
      className="oph-dash-section bd-drill"
      aria-label={`${d?.name ?? id} details`}
      data-testid="buddy-drill"
    >
      <div className="oph-dash-section-head">
        <h2>
          {d?.name ?? id} <span className="nyk-ui-hint">{GROUP_LABELS[kind].toLowerCase()}</span>
        </h2>
        <Button size="sm" variant="ghost" icon={<X size={14} />} onClick={onClose}>
          Close
        </Button>
      </div>
      <ErrorBox error={res.error} />
      <Loading loading={res.loading && !d} />
      {d && (
        <>
          {(d.monthlyLimit != null || d.isUnlimited) && (
            <p>
              <span className="nyk-ui-eyebrow">Month limit </span>
              <LimitMeter
                used={d.usedThisMonth ?? d.totals.weightedTokens}
                limit={d.monthlyLimit}
                unlimited={d.isUnlimited}
                projected={d.projectedThisMonth}
              />
            </p>
          )}
          <KpiGrid totals={d.totals} compare={!!query.compare} />
          <DailyCharts s={d} from={d.period.from} to={d.period.to} />
          <div className="bd-grid">
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Per user</h2>
                <span className="nyk-ui-hint">{d.byUser.length}</span>
              </div>
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="buddy-drill-users">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th className="nyk-ui-num">Questions</th>
                      <th className="nyk-ui-num">Tokens</th>
                      <th className="nyk-ui-num">Cost</th>
                      <th>Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.byUser.map((u) => (
                      <tr key={u.userCode}>
                        <td className="nyk-card-title">
                          {u.userCode}
                          {u.isAgent && (
                            <Chip size="sm" className="nyk-tone-blue">
                              agent
                            </Chip>
                          )}
                        </td>
                        <td className="nyk-ui-num" data-label="Questions">
                          {u.questions == null ? '—' : fmt(u.questions)}
                        </td>
                        <td className="nyk-ui-num" data-label="Tokens">
                          {compact(u.tokens)}
                        </td>
                        <td className="nyk-ui-num" data-label="Cost">
                          {money(u.cost)}
                        </td>
                        <td data-label="Last active">
                          {u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
              {!d.byUser.length && <EmptyState title="Nobody asked anything in this period." />}
            </div>
            {children ? (
              <div className="nyk-card">
                <div className="nyk-card-head">
                  <h2>{children.title}</h2>
                </div>
                <TableScroller>
                  <table className="nyk-ui-table nyk-cards">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th className="nyk-ui-num">Questions</th>
                        <th className="nyk-ui-num">Tokens</th>
                        <th className="nyk-ui-num">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {children.rows.map((r) => (
                        <tr key={r.key}>
                          <td className="nyk-card-title">{r.name || r.key}</td>
                          <td className="nyk-ui-num" data-label="Questions">
                            {r.questions == null ? '—' : fmt(r.questions)}
                          </td>
                          <td className="nyk-ui-num" data-label="Tokens">
                            {compact(r.tokens)}
                          </td>
                          <td className="nyk-ui-num" data-label="Cost">
                            {money(r.cost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroller>
              </div>
            ) : (
              <div className="nyk-card">
                <div className="nyk-card-head">
                  <h2>Recent conversations</h2>
                </div>
                <ul className="oph-attention">
                  {(d.recentConversations ?? []).map((c, i) => (
                    <li key={c.id ?? i}>
                      <span className="oph-attention-name">
                        {c.title || '(untitled)'}
                        <span className="nyk-ui-hint">
                          {' '}
                          {c.userCode} · {c.messageCount} messages
                        </span>
                      </span>
                      <span className="oph-attention-detail">{new Date(c.updatedAt).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
                {!d.recentConversations?.length && <EmptyState title="No conversations in this period." />}
              </div>
            )}
          </div>
          <SeriesCharts s={d} timezone={d.timezone} />
        </>
      )}
    </section>
  );
}

/** nyce.buddy usage of one environment (`?target=`): KPIs, trends, per group with limits, drill-down per scope. */
export function BuddyUsagePage() {
  const { active } = useTargets();
  const { period, groupBy, from, to, compare, drill, set } = useUsageParams();
  const rangeError = period === 'custom' ? customRangeError(from, to) : null;
  const query = useMemo<UsageQuery>(
    () =>
      period === 'custom'
        ? { period, groupBy, from, to, compare: compare || undefined }
        : { period, groupBy, compare: compare || undefined },
    [period, groupBy, from, to, compare]
  );
  const usage = useAsync(
    () => (rangeError ? Promise.resolve(null) : buddyFor(active).usage(query)),
    [active, JSON.stringify(query), rangeError]
  );
  const u = usage.data;
  const label = periodLabel(period, from, to);
  const drillKind = DRILL[groupBy];
  const exportCsv = () => {
    if (!u) return;
    const rows = u.groups.map((g) => {
      const t = g.totals ?? g;
      return {
        [groupBy]: g.name,
        id: g.id,
        questions: g.questions,
        users: t.humanUsers ?? g.activeUsers,
        tokens: g.tokens,
        nyceTokens: g.weightedTokens,
        cost: g.cost,
        requests: g.requests,
        monthlyLimit: g.monthlyLimit ?? '',
        usedThisMonth: g.usedThisMonth ?? '',
        projectedThisMonth: g.projectedThisMonth ?? '',
        previousQuestions: t.previous?.questions ?? '',
      };
    });
    const cols = Object.keys(rows[0] ?? { [groupBy]: '' });
    const esc = (v: unknown) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      cols.join(','),
      ...rows.map((r) => cols.map((c) => esc(r[c as keyof typeof r])).join(',')),
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `nycebuddy-${active}-${groupBy}-${period === 'custom' ? `${from}_${to}` : period}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <Page
      title="Buddy usage"
      description="nyce.buddy questions, users, tokens, cost and quality for the chosen period, per organization, system, user or model."
      actions={
        <div className="bd-toolbar">
          <Select
            aria-label="Period"
            value={period}
            onChange={(v) => set({ period: v === 'month' ? null : v })}
            options={PERIODS.map((p) => ({ value: p.value, label: p.label }))}
          />
          {period === 'custom' && (
            <>
              <input
                type="date"
                aria-label="From"
                className="nyk-ui-input"
                value={from}
                onChange={(e) => set({ from: e.target.value })}
              />
              <input
                type="date"
                aria-label="To"
                className="nyk-ui-input"
                value={to}
                onChange={(e) => set({ to: e.target.value })}
              />
            </>
          )}
          <Select
            aria-label="Group by"
            value={groupBy}
            onChange={(v) => set({ groupBy: v === 'organization' ? null : v, drill: null })}
            options={(Object.keys(GROUP_LABELS) as UsageGroupBy[]).map((g) => ({
              value: g,
              label: `by ${GROUP_LABELS[g].toLowerCase()}`,
            }))}
          />
          <Switch checked={compare} onChange={(c) => set({ compare: c ? '1' : null })} label="Compare" />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={usage.loading}
            onClick={() => void usage.reload()}
          >
            Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={exportCsv} disabled={!u?.groups.length}>
            CSV
          </Button>
        </div>
      }
    >
      {rangeError && <ErrorBox error={rangeError} />}
      <ErrorBox error={usage.error} />
      <Loading loading={usage.loading && !u} />
      {u && (
        <div className={usage.loading ? 'oph-dash-stale' : undefined} aria-busy={usage.loading}>
          <p className="nyk-ui-hint">
            {label}
            {u.timezone && ` · hours in ${u.timezone}`}
            {compare &&
              u.period.previous &&
              ` · compared with ${u.period.previous.from} – ${u.period.previous.to}`}
          </p>
          <KpiGrid totals={u.totals} compare={compare} />
          <DailyCharts s={u} from={u.period.from} to={u.period.to} />
          <GroupTable
            groups={u.groups}
            groupBy={groupBy}
            compare={compare}
            onOpen={drillKind ? (g) => set({ drill: `${drillKind}:${g.id}` }) : undefined}
          />
          {drill && (
            <DrillDown
              target={active}
              kind={drill.kind}
              id={drill.id}
              query={query}
              onClose={() => set({ drill: null })}
            />
          )}
          <SeriesCharts s={u} timezone={u.timezone} />
        </div>
      )}
    </Page>
  );
}
